import { ref, computed } from 'vue';

// Estado global compartilhado entre componentes
const isRegionReady = ref(false);
const currentRegion = ref(null);

export function useRegion() {
  /**
   * Marca a região como pronta para uso
   */
  const setRegionReady = (ready = true) => {
    isRegionReady.value = ready;
  };

  /**
   * Atualiza a região atual
   */
  const setCurrentRegion = (region) => {
    currentRegion.value = region;
    if (region) {
      setRegionReady(true);
    }
  };

  /**
   * Aguarda até que a região esteja pronta
   * @param {number} timeout - Tempo máximo de espera em ms (padrão: 5000)
   * @returns {Promise<boolean>} - true se região está pronta, false se timeout
   */
  const waitForRegion = (timeout = 5000) => {
    return new Promise((resolve) => {
      if (isRegionReady.value) {
        resolve(true);
        return;
      }

      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (isRegionReady.value) {
          clearInterval(checkInterval);
          resolve(true);
        } else if (Date.now() - startTime > timeout) {
          clearInterval(checkInterval);
          resolve(false);
        }
      }, 50);
    });
  };

  /**
   * Obtém a região do localStorage
   */
  const getStoredRegion = () => {
    try {
      const stored = localStorage.getItem('currentRegion');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Erro ao ler região do localStorage:', e);
    }
    return null;
  };

  /**
   * Verifica se há uma região válida
   */
  const hasValidRegion = computed(() => {
    return currentRegion.value !== null || getStoredRegion() !== null;
  });

  /**
   * Reseta o estado da região
   */
  const resetRegion = () => {
    isRegionReady.value = false;
    currentRegion.value = null;
  };

  return {
    isRegionReady: computed(() => isRegionReady.value),
    currentRegion: computed(() => currentRegion.value),
    hasValidRegion,
    setRegionReady,
    setCurrentRegion,
    waitForRegion,
    getStoredRegion,
    resetRegion
  };
}
